import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";

function Cart() {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, membership } = useCart();

  const serviceItems = useMemo(
    () => cartItems.filter((item) => item.type === "service"),
    [cartItems]
  );

  const materialItems = useMemo(
    () => cartItems.filter((item) => item.type === "material"),
    [cartItems]
  );

  const subtotal = cartItems.reduce(
    (sum, item) => sum + Number(item.finalPrice || 0),
    0
  );

  return (
    <div style={page}>
      <div style={container} className="cart-page__container">
        <button onClick={() => navigate("/")} style={backBtn} className="cart-page__back-button">
          ← Back to Home
        </button>

        <div style={heroCard} className="cart-page__hero">
          <div>
            <h1 style={heroTitle} className="cart-page__title">Your Cart</h1>
            <p style={heroText} className="cart-page__hero-text">
              Review your selected services and materials before moving to checkout.
            </p>
          </div>

          <div style={heroStatWrap} className="cart-page__hero-stats">
            <div style={heroStatCard}>
              <div style={heroStatLabel}>Items</div>
              <div style={heroStatValue}>{cartItems.length}</div>
            </div>
            <div style={heroStatCard}>
              <div style={heroStatLabel}>Subtotal</div>
              <div style={heroStatValue}>₹{subtotal}</div>
            </div>
          </div>
        </div>

        {membership ? (
          <div style={membershipBanner}>
            <div style={{ fontWeight: "bold" }}>
              Membership active: {membership.name}
            </div>
            <div style={{ color: "#bdbdbd", marginTop: "4px" }}>
              Add, change, or remove the plan again during checkout. Membership gives 10% cashback after eligible completed orders, not an upfront fare discount.
            </div>
          </div>
        ) : (
          <div style={membershipBannerMuted} className="cart-page__membership-muted">
            <div style={{ fontWeight: "bold" }}>No membership selected</div>
            <div style={{ color: "#bdbdbd", marginTop: "4px" }}>
              Membership can be added at checkout and gives 10% cashback after eligible completed orders.
            </div>
            <button
              onClick={() => navigate("/membership")}
              style={lightButton}
            >
              View Membership
            </button>
          </div>
        )}

        {cartItems.length === 0 ? (
          <div style={emptyState}>
            <div style={{ fontSize: "42px" }}>🛒</div>
            <h2 style={{ marginBottom: "8px" }}>Your cart is empty</h2>
            <p style={{ color: "#bdbdbd", marginTop: 0 }}>
              Add a service or material to continue.
            </p>

            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginTop: "16px" }} className="cart-page__empty-actions">
              <button onClick={() => navigate("/all-services")} style={primaryButton} className="cart-page__primary-button">
                Explore Services
              </button>
              <button onClick={() => navigate("/materials")} style={secondaryButton} className="cart-page__secondary-button">
                Browse Materials
              </button>
            </div>
          </div>
        ) : (
          <>
            {serviceItems.length > 0 && (
              <section style={{ marginTop: "26px" }}>
                <h2 style={sectionTitle}>Service Items</h2>
                <div style={grid} className="cart-page__grid">
                  {serviceItems.map((item, index) => (
                    <div key={`service-${index}`} style={card}>
                      <div style={badge}>Service</div>
                      <h3 style={cardTitle}>{item.title}</h3>

                      {item.serviceCategory && (
                        <p style={mutedLine}>
                          <strong>Category:</strong> {item.serviceCategory}
                        </p>
                      )}

                      {item.description && (
                        <p style={mutedLine}>{item.description}</p>
                      )}

                      {item.warranty && (
                        <p style={mutedLine}>
                          <strong>Warranty:</strong> {item.warranty}
                        </p>
                      )}

                      <p style={priceLine}>₹{item.finalPrice}</p>

                      <button
                        onClick={() => removeFromCart(cartItems.indexOf(item))}
                        style={dangerButton}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {materialItems.length > 0 && (
              <section style={{ marginTop: "30px" }}>
                <h2 style={sectionTitle}>Material Items</h2>
                <div style={grid} className="cart-page__grid">
                  {materialItems.map((item, index) => (
                    <div key={`material-${index}`} style={card}>
                      <div style={badge}>Material</div>
                      <h3 style={cardTitle}>{item.title}</h3>

                      {item.description && (
                        <p style={mutedLine}>{item.description}</p>
                      )}

                      <p style={mutedLine}>
                        <strong>Quantity:</strong> {item.quantity || 1}
                      </p>

                      {item.unit && (
                        <p style={mutedLine}>
                          <strong>Unit:</strong> {item.unit}
                        </p>
                      )}

                      {item.shopName && (
                        <p style={mutedLine}>
                          <strong>Shop:</strong> {item.shopName}
                        </p>
                      )}

                      <p style={priceLine}>₹{item.finalPrice}</p>

                      <button
                        onClick={() => removeFromCart(cartItems.indexOf(item))}
                        style={dangerButton}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <div style={summaryCard} className="cart-page__summary-card">
              <div>
                <h3 style={{ marginTop: 0 }}>Cart Summary</h3>
                <p style={mutedLine}>
                  Service items: {serviceItems.length} • Material items: {materialItems.length}
                </p>
                <p style={summaryTotal}>Subtotal: ₹{subtotal}</p>
              </div>

              <div style={ctaRow} className="cart-page__cta-row">
                <button
                  onClick={() => navigate("/checkout")}
                  style={primaryButton}
                  className="cart-page__primary-button"
                >
                  Proceed to Checkout
                </button>
                <button
                  onClick={() => navigate("/all-services")}
                  style={secondaryButton}
                  className="cart-page__secondary-button"
                >
                  Add More Services
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const page = {
  background: "#000",
  minHeight: "100vh",
  color: "#fff",
  padding: "24px 16px 80px",
};

const container = {
  maxWidth: "1180px",
  margin: "0 auto",
};

const backBtn = {
  marginBottom: "18px",
  padding: "10px 14px",
  border: "1px solid #2e2e2e",
  background: "#111",
  color: "#fff",
  borderRadius: "10px",
  cursor: "pointer",
};

const heroCard = {
  background: "linear-gradient(135deg, #111, #1a1a1a)",
  border: "1px solid #242424",
  borderRadius: "22px",
  padding: "24px",
  display: "flex",
  justifyContent: "space-between",
  gap: "18px",
  flexWrap: "wrap",
  boxShadow: "0 16px 40px rgba(0,0,0,0.28)",
};

const heroTitle = {
  margin: 0,
  fontSize: "34px",
};

const heroText = {
  marginTop: "10px",
  color: "#bdbdbd",
  lineHeight: "1.7",
  maxWidth: "650px",
};

const heroStatWrap = {
  display: "flex",
  gap: "12px",
  flexWrap: "wrap",
};

const heroStatCard = {
  background: "#0d0d0d",
  border: "1px solid #242424",
  borderRadius: "16px",
  padding: "16px 18px",
  minWidth: "130px",
};

const heroStatLabel = {
  color: "#8f8f8f",
  fontSize: "13px",
};

const heroStatValue = {
  fontSize: "24px",
  fontWeight: "bold",
  marginTop: "6px",
};

const membershipBanner = {
  marginTop: "18px",
  background: "rgba(20,70,35,0.45)",
  border: "1px solid #28593a",
  borderRadius: "18px",
  padding: "18px",
};

const membershipBannerMuted = {
  marginTop: "18px",
  background: "#111",
  border: "1px solid #262626",
  borderRadius: "18px",
  padding: "18px",
  display: "flex",
  justifyContent: "space-between",
  gap: "12px",
  alignItems: "center",
  flexWrap: "wrap",
};

const emptyState = {
  marginTop: "28px",
  background: "#111",
  border: "1px solid #262626",
  borderRadius: "24px",
  padding: "40px 24px",
  textAlign: "center",
};

const sectionTitle = {
  fontSize: "26px",
  marginBottom: "14px",
};

const grid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  gap: "16px",
};

const card = {
  background: "linear-gradient(180deg, #111, #0b0b0b)",
  border: "1px solid #242424",
  borderRadius: "20px",
  padding: "18px",
  boxShadow: "0 12px 30px rgba(0,0,0,0.22)",
};

const badge = {
  display: "inline-block",
  padding: "6px 10px",
  borderRadius: "999px",
  background: "#1b1b1b",
  border: "1px solid #2b2b2b",
  color: "#d8d8d8",
  fontSize: "12px",
  marginBottom: "12px",
};

const cardTitle = {
  marginTop: 0,
  marginBottom: "12px",
  fontSize: "20px",
};

const mutedLine = {
  color: "#c7c7c7",
  lineHeight: "1.6",
  margin: "6px 0",
};

const priceLine = {
  marginTop: "14px",
  fontSize: "22px",
  fontWeight: "bold",
};

const summaryCard = {
  marginTop: "30px",
  background: "linear-gradient(135deg, #141414, #0f0f0f)",
  border: "1px solid #252525",
  borderRadius: "22px",
  padding: "22px",
  display: "flex",
  justifyContent: "space-between",
  gap: "18px",
  alignItems: "center",
  flexWrap: "wrap",
};

const summaryTotal = {
  fontSize: "24px",
  fontWeight: "bold",
  marginBottom: 0,
};

const ctaRow = {
  display: "flex",
  gap: "12px",
  flexWrap: "wrap",
};

const primaryButton = {
  padding: "12px 18px",
  border: "none",
  background: "#fff",
  color: "#000",
  borderRadius: "12px",
  cursor: "pointer",
  fontWeight: "bold",
};

const secondaryButton = {
  padding: "12px 18px",
  border: "1px solid #3a3a3a",
  background: "#111",
  color: "#fff",
  borderRadius: "12px",
  cursor: "pointer",
};

const lightButton = {
  padding: "10px 16px",
  border: "none",
  background: "#fff",
  color: "#000",
  borderRadius: "10px",
  cursor: "pointer",
  fontWeight: "bold",
};

const dangerButton = {
  marginTop: "14px",
  padding: "10px 14px",
  border: "1px solid #6b1d1d",
  background: "#1a0c0c",
  color: "#ff9898",
  borderRadius: "10px",
  cursor: "pointer",
};

export default Cart;
